import './TQServiceRequestTitle.scss';

import {
  useEffect,
  useState,
} from 'react';

import {
  Button,
  Select,
} from 'antd';
import { PiArrowRight } from 'react-icons/pi';
import { BottomSheet } from 'react-spring-bottom-sheet';

import Header from '../../../../components/Header/Header';
import {
  setServiceRequestsFilters,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../shared/hooks';
import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import { ITQTagItem } from '../../../../types/tqConnectTypes/service-requests';
import SelectTQConnectTags from '../SelectTQConnectTags/SelectTQConnectTags';
import TQCreateNewRequestButton from '../TQCreateNewRequestButton';

export type CustomiseTopicsFilterType = 'filter_by_tags' | 'ignore_tags';

export type CustomiseTopicsMode = 'include' | 'exclude';

export interface CustomiseTopicsRequestBody {
  filterType: CustomiseTopicsFilterType;
  watchedTags: string[];
}

const TQServiceRequestTitle: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isTQConnectEnabled, userAccessType } = checkTQConnectAccess();
  const {
    serviceRequestsFilters: { includedTags, excludedTags },
  } = useAppSelector((state) => state.tqConnectServiceRequest);

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<CustomiseTopicsMode>('include');
  const [tagsToInclude, setTagsToInclude] = useState<ITQTagItem[]>(
    [] as ITQTagItem[],
  );
  const [tagsToExclude, setTagsToExclude] = useState<ITQTagItem[]>(
    [] as ITQTagItem[],
  );
  const [displayTagsSelector, setDisplayTagsSelector] =
    useState<boolean>(false);

  const convertTagsToSelectOptions = (tags: ITQTagItem[]) => {
    return tags.map((tag) => ({
      ...tag,
      key: tag._id,
      value: tag._id,
      label: tag.name,
    }));
  };

  const hasChanges =
    JSON.stringify([...(tagsToInclude || [])].sort()) !==
      JSON.stringify([...(includedTags || [])].sort()) ||
    JSON.stringify([...(tagsToExclude || [])].sort()) !==
      JSON.stringify([...(excludedTags || [])].sort());

  const handleSave = (clear: boolean = false) => {
    dispatch(
      setServiceRequestsFilters({
        includedTags: clear ? [] : tagsToInclude,
        excludedTags: clear ? [] : tagsToExclude,
      }),
    );
    setIsOpen(false);
    setDisplayTagsSelector(false);
    setMode('include');
    if (clear) {
      setTagsToInclude([]);
      setTagsToExclude([]);
    }
  };

  const handleClear = () => {
    setIsOpen(false);
    setMode('include');
    setDisplayTagsSelector(false);
    setTagsToInclude(includedTags || []);
    setTagsToExclude(excludedTags || []);
  };

  useEffect(() => {
    setTagsToInclude(includedTags || []);
    setTagsToExclude(excludedTags || []);
  }, [includedTags, excludedTags]);

  const renderTagsSelector = () => {
    switch (mode) {
      case 'include':
        return (
          <SelectTQConnectTags
            value={convertTagsToSelectOptions(tagsToInclude)}
            onChange={(value) =>
              setTagsToInclude(
                value?.map(({ key, label, value: value2, ...tag }) => ({
                  ...tag,
                  key: value2,
                  _id: value2,
                  name: label,
                })),
              )
            }
          />
        );
      case 'exclude':
        return (
          <SelectTQConnectTags
            value={convertTagsToSelectOptions(tagsToExclude)}
            onChange={(value) =>
              setTagsToExclude(
                value?.map(({ key, label, value: value2, ...tag }) => ({
                  ...tag,
                  key: value2,
                  _id: value2,
                  name: label,
                })),
              )
            }
          />
        );
      default:
        return null;
    }
  };

  const headerTitle = displayTagsSelector ? 'Select tags' : 'Customise topics';
  const headerBackType = displayTagsSelector ? 'arrow' : 'cross';
  const headerHandleBack = displayTagsSelector
    ? () => setDisplayTagsSelector(false)
    : () => handleClear();

  return (
    <>
      <div className="service-requests-discover__header">
        <div className="service-requests-discover__header_content">
          <p className="service-requests-discover__header_content_title">
            Discover Service Requests
          </p>
          <span className="service-requests-discover__header_content_subtitle">
            Browse all requests or customise topics to match your expertise.
          </span>
        </div>
        <div className="service-requests-discover__header_button">
          <Button
            className="service-requests-discover__header_button_customise"
            onClick={() => setIsOpen(true)}>
            <p className="service-requests-discover__header_button_customise_text">
              Customise topics
            </p>
            <PiArrowRight />
          </Button>
          {includedTags?.length || excludedTags?.length ? (
            <div className="service-requests-discover__header_button_customise_clear_container">
              <p className="service-requests-discover__header_button_customise_clear_text">
                {includedTags?.length || 0} watched, {excludedTags?.length || 0}{' '}
                ignored
              </p>
              <Button
                className="service-requests-discover__header_button_customise_clear_button"
                onClick={() => {
                  handleSave(true);
                }}>
                Clear
              </Button>
            </div>
          ) : null}
        </div>
        {isTQConnectEnabled && userAccessType === 'customer' ? (
          <TQCreateNewRequestButton />
        ) : null}
      </div>

      <BottomSheet
        open={isOpen}
        onDismiss={() => {
          handleClear();
        }}
        header={
          <Header
            title={headerTitle}
            canGoBack
            handleBack={headerHandleBack}
            backType={headerBackType}
            noBorder
            actionItems={
              displayTagsSelector
                ? [
                    <Button
                      key="done"
                      className="accentBtnV1 siteBtn"
                      onClick={() => setDisplayTagsSelector(false)}>
                      Done
                    </Button>,
                  ]
                : []
            }
          />
        }>
        <div className="customise-topics-sheet">
          {displayTagsSelector ? (
            renderTagsSelector()
          ) : (
            <>
              <div className="customise-topics-sheet__toggle-group">
                <button
                  type="button"
                  className={`customise-topics-sheet__toggle ${
                    mode === 'include'
                      ? 'customise-topics-sheet__toggle--active'
                      : ''
                  }`}
                  onClick={() => setMode('include')}>
                  Filter by tags
                </button>
                <button
                  type="button"
                  className={`customise-topics-sheet__toggle ${
                    mode === 'exclude'
                      ? 'customise-topics-sheet__toggle--active'
                      : ''
                  }`}
                  onClick={() => setMode('exclude')}>
                  Ignore tags
                </button>
              </div>

              <h3 className="customise-topics-sheet__section-title">
                {mode === 'exclude' ? 'Ignored tags' : 'Watched tags'}
              </h3>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select tags"
                onFocus={() => setDisplayTagsSelector(true)}
                options={[]}
                value={
                  mode === 'exclude'
                    ? convertTagsToSelectOptions(tagsToExclude)
                    : convertTagsToSelectOptions(tagsToInclude)
                }
                open={false}
                onSelect={() => setDisplayTagsSelector(true)}
                onDeselect={() => setDisplayTagsSelector(true)}
              />

              <div className="customise-topics-sheet__actions">
                <button
                  type="button"
                  className={`customise-topics-sheet__save-btn ${
                    hasChanges
                      ? 'customise-topics-sheet__save-btn--active'
                      : 'customise-topics-sheet__save-btn--disabled'
                  }`}
                  disabled={!hasChanges}
                  onClick={() => handleSave(false)}>
                  Save Changes
                </button>
              </div>
            </>
          )}
        </div>
      </BottomSheet>
    </>
  );
};

export default TQServiceRequestTitle;
